import {useMemo, useState} from 'react';

import {FilterDialogItemId, FilterDialogSectionId} from 'filters/features/FilterDialog/types';
import {
  DatasourceType,
  InclusionDateType,
  PeriodType,
  PolicyAmountType,
  PolicyStatusType,
} from 'modules/decisionsNext/api/types';
import {DateRangeFieldValue} from 'features/GlobalFilterProvider/filters/period/DateRangeItem/types';
import {assertPolicyFilter, isPolicyFilter} from 'filters/types';
import {FilterTopbarItemId} from 'filters/features/FilterTopbar/types';

import {openFilterDialog} from 'filters';
import {getLabelByValue, neverCase} from 'utils/misc';
import {DATE_FORMAT, dayjs} from 'utils/dateUtils';
import {createRadioGroupItem as createSectionRadioGroupItem} from 'filters/features/FilterDialog/sections/ItemsSection/items/RadioGroupItem/RadioGroupItem';
import {createItemsSection} from 'filters/features/FilterDialog/sections/ItemsSection/ItemsSection';
import {createDateRangeItem} from 'features/GlobalFilterProvider/filters/period/DateRangeItem';
import {createRadioGroupItem as createTopbarRadioGroupItem} from 'filters/features/FilterTopbar/items/RadioGroupItem/RadioGroupItem';
import {createValueItem} from 'filters/features/FilterTopbar/items/ValueItem/ValueItem';
import {createPolicyStatusItem} from 'features/GlobalFilterProvider/filters/period/PolicyStatusItem/PolicyStatusItem';
import {useFullPolicyStatusTree} from 'modules/decisionsNext/hooks/trees/usePolicyStatusesTree';
import {yup} from 'utils/yup';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {formatPolicyFilterPeriod} from 'features/GlobalFilterProvider/utils/formatters';
import {createCounterItem} from 'filters/features/FilterTopbar/items/CounterItem/CounterItem';
import {formatDateRangeValue} from 'features/GlobalFilterProvider/filters/period/utils';

import CalendarIcon from 'components/icons/calendar.svg';

import {SeparatedLabel} from 'modules/decisionsNext/components/BaseFilter/TopBar/components/SeparatedLabel';
import {initialFilter} from 'modules/decisionsNext/components/BaseFilter/utils/filter';
import {
  amountTypeOptions,
  getPeriodTypeSeparatedLabels,
  hasInvalidDateRange,
  policyStatusOptions,
  productionAllocationOptions,
} from 'modules/decisionsNext/components/BaseFilter/utils/period';
import {
  inforceMinDateValidation,
  inforceMaxDateValidation,
} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/DateRangeBlock/InforceDatePicker';

export const premiumTypeDialogItem = createSectionRadioGroupItem({
  id: FilterDialogItemId.PremiumType,
  label: 'Premium Type',
  options: amountTypeOptions,
  getValue: filter => {
    assertPolicyFilter(filter);

    return filter.period?.amountType ?? PolicyAmountType.Billed;
  },
  onChange: (filter, value) => {
    assertPolicyFilter(filter);

    return {
      ...filter,
      period: formatPolicyFilterPeriod({
        ...filter.period,
        type: value === PolicyAmountType.Billed ? PeriodType.Rolling_12Months : PeriodType.InForce,
        timestamp: value === PolicyAmountType.Billed ? filter.period?.timestamp : undefined,
        inclusionDateType:
          value === PolicyAmountType.Billed ? InclusionDateType.Transaction : filter.period?.inclusionDateType,
        amountType: value,
      }),
      datasourceType: value === PolicyAmountType.AnnualizedWritten ? DatasourceType.AgencyData : filter.datasourceType,
    };
  },
});

const dateRangeDialogItem = createDateRangeItem({
  id: FilterDialogItemId.DateRange,
  getActiveFiltersContent: () => null,
  validate: yup
    .object()
    .shape({
      type: yup.mixed<PeriodType>().required('Period type is required'),
      from: yup.string().nullable(),
      to: yup.string().nullable(),
    })
    .test({
      name: 'valid-date-range',
      skipAbsent: true,
      test: (value: DateRangeFieldValue, ctx) => {
        if (value.additionalPeriod) {
          if (hasInvalidDateRange(value.additionalPeriod.from, value.additionalPeriod.to)) {
            return ctx.createError({message: 'Invalid additional filter'});
          }
        }

        switch (value.type) {
          case PeriodType.AllTime:
            return ctx.createError({message: 'Invalid period type'});
          case PeriodType.Ytd:
          case PeriodType.Rolling_12Months:
            return true;

          case PeriodType.InForce: {
            if (!value.timestamp && !value.inForceToday) {
              return ctx.createError({message: 'Timestamp is required'});
            }

            if (value.inForceToday) {
              return true;
            }

            const date = dayjs(value.timestamp);

            if (!date.isValid()) {
              return ctx.createError({message: 'Invalid timestamp'});
            }

            if (date.isBefore(inforceMinDateValidation) || date.isAfter(inforceMaxDateValidation)) {
              return ctx.createError({message: 'Timestamp is out of valid range'});
            }

            return true;
          }

          case PeriodType.FromTo: {
            return hasInvalidDateRange(value.from, value.to, value.inclusionDateType !== InclusionDateType.Expiration)
              ? ctx.createError({message: 'Invalid date range'})
              : true;
          }

          default:
            neverCase(value.type);
        }
      },
    }),
  getValue: filter => {
    assertPolicyFilter(filter);

    return formatDateRangeValue({
      type: filter.period?.type ?? initialFilter.period.type,
      inclusionDateType: filter.period?.inclusionDateType ?? initialFilter.period.inclusionDateType,
      from: filter.period?.from,
      to: filter.period?.to,
      timestamp: filter.period?.timestamp,
      additionalPeriod: filter.additionalPeriods
        ? {
            from: filter.additionalPeriods[0]?.from ?? undefined,
            to: filter.additionalPeriods[0]?.to ?? undefined,
          }
        : undefined,
    });
  },
  onChange: (filter, value) => {
    assertPolicyFilter(filter);

    return {
      ...filter,
      period: formatPolicyFilterPeriod({
        ...filter.period,
        type: value.type,
        inclusionDateType: value.inclusionDateType,
        from: value.from,
        to: value.to,
        timestamp: value.inForceToday ? undefined : value.timestamp,
      }),
      additionalPeriods: value.additionalPeriod
        ? [
            {
              type: PeriodType.FromTo,
              from: value.additionalPeriod.from,
              to: value.additionalPeriod.to,
            },
          ]
        : undefined,
    };
  },
});

const datasourceTypeDialogItem = createSectionRadioGroupItem({
  id: FilterDialogItemId.DatasourceType,
  label: 'Production Allocation',
  options: [
    {
      label: 'Producer Production Credit',
      value: DatasourceType.ProductionCredit,
      annotation: 'Premium/revenue aggregated by producer production credit.',
    },
    {
      label: 'Primary Producer',
      value: DatasourceType.AgencyData,
      annotation: 'Premium/revenue aggregated by primary producer',
    },
  ],
  getValue: filter => {
    assertPolicyFilter(filter);

    return filter.datasourceType;
  },
  onChange: (filter, value) => {
    assertPolicyFilter(filter);

    return {
      ...filter,
      datasourceType: value,
    };
  },
});

const policyStatusDialogItem = createPolicyStatusItem({
  id: FilterDialogItemId.PolicyStatus,
  getValue: filter => {
    assertPolicyFilter(filter);

    return {
      statusFilter: filter.policyStatusesAnd?.[0] ?? undefined,
      type: filter.policyStatusType ?? undefined,
    };
  },
  onChange: (filter, value) => {
    assertPolicyFilter(filter);

    return {
      ...filter,
      policyStatusesAnd: value.statusFilter ? [value.statusFilter] : undefined,
      policyStatusType: value.type,
    };
  },
  getActiveFiltersContent: () => null,
});

// FIXME: Remove formatDateRangeValue, add timestamp in onChange / onApply fn
export const usePeriodDialogSection = () => {
  const withPolicyStatusFilter = useFeatureFlag(FeatureFlags.PolicyStatusFilter);
  const [skip, setSkip] = useState(true);
  const {treeData} = useFullPolicyStatusTree(skip);

  return useMemo(
    () =>
      createItemsSection({
        id: FilterDialogSectionId.DateAndType,
        title: 'Date & Type',
        icon: CalendarIcon,
        collapsibleActiveFilterSection: false,
        // We add undefined for values for override of overrideActiveFilterContent
        overrideActiveFilterContent: (filter, setFilter) => {
          const onClick = () => openFilterDialog(FilterDialogSectionId.DateAndType);

          const premiumTypeValue = premiumTypeDialogItem.getValue(filter);
          const dateRangeValue = dateRangeDialogItem.getValue(filter);
          const datasourceTypeValue = datasourceTypeDialogItem.getValue(filter);
          const policyStatusValue = policyStatusDialogItem.getValue(filter);

          if (policyStatusValue.statusFilter) {
            setSkip(false);
          }

          return [
            {
              id: 'date-range',
              chips: [
                {
                  id: 'premium-type',
                  label: getLabelByValue(amountTypeOptions, premiumTypeValue),
                  onClick,
                  onDelete:
                    premiumTypeValue !== initialFilter.period.amountType
                      ? () => setFilter(prev => premiumTypeDialogItem.onChange(prev, initialFilter.period.amountType))
                      : undefined,
                },
                dateRangeValue && {
                  id: 'date-range',
                  label: <SeparatedLabel labels={getPeriodTypeSeparatedLabels(dateRangeValue)} />,
                  onClick,
                  onDelete:
                    dateRangeValue.type !== initialFilter.period.type ||
                    dateRangeValue.inclusionDateType !== initialFilter.period.inclusionDateType
                      ? () =>
                          setFilter(prev =>
                            dateRangeDialogItem.onChange(prev, {
                              ...dateRangeValue,
                              type: initialFilter.period.type,
                              inclusionDateType: initialFilter.period.inclusionDateType,
                            })
                          )
                      : undefined,
                },
                dateRangeValue?.additionalPeriod && {
                  id: 'additional-period',
                  label: `Tr ${dayjs(dateRangeValue.additionalPeriod.from).format('L')} - ${dayjs(
                    dateRangeValue.additionalPeriod.to
                  ).format('L')}`,
                  onClick,
                  onDelete: () =>
                    setFilter(prev =>
                      dateRangeDialogItem.onChange(prev, {
                        ...dateRangeValue,
                        additionalPeriod: null,
                      })
                    ),
                },
                {
                  id: 'datasource-type',
                  label: getLabelByValue(productionAllocationOptions, datasourceTypeValue),
                  onClick,
                  onDelete:
                    datasourceTypeValue !== initialFilter.datasourceType
                      ? () => setFilter(prev => datasourceTypeDialogItem.onChange(prev, initialFilter.datasourceType))
                      : undefined,
                },
                withPolicyStatusFilter && {
                  id: 'policy-status',
                  label: getLabelByValue(policyStatusOptions, policyStatusValue?.type),
                  onClick,
                  onDelete:
                    policyStatusValue?.type !== initialFilter.policyStatusType
                      ? () =>
                          setFilter(prev =>
                            policyStatusDialogItem.onChange(prev, {
                              statusFilter: undefined,
                              type: initialFilter.policyStatusType,
                            })
                          )
                      : undefined,
                },
              ].filter(Boolean),
            },
            policyStatusValue?.statusFilter?.statuses?.length && {
              id: 'policy-statuses',
              label: 'Policy Statuses',
              chips: policyStatusValue?.statusFilter?.statuses?.map(status => ({
                id: status,
                label: treeData.find(s => s.key === status)?.title || status,
                onClick,
                onDelete: () => {
                  const filteredStatuses = policyStatusValue?.statusFilter?.statuses?.filter(s => s !== status);
                  const codes = policyStatusValue?.statusFilter?.codes;

                  setFilter(prev =>
                    policyStatusDialogItem.onChange(prev, {
                      ...prev,
                      statusFilter:
                        filteredStatuses?.length || codes?.length ? {statuses: filteredStatuses, codes} : undefined,
                    })
                  );
                },
              })),
            },
            policyStatusValue?.statusFilter?.codes?.length && {
              id: 'policy-status-codes',
              label: 'Policy AMS Codes',
              chips: policyStatusValue?.statusFilter?.codes?.map(code => ({
                id: code,
                label: code,
                onClick,
                onDelete: () => {
                  const filteredCodes = policyStatusValue?.statusFilter?.codes?.filter(c => c !== code);
                  const statuses = policyStatusValue?.statusFilter?.statuses;

                  setFilter(prev =>
                    policyStatusDialogItem.onChange(prev, {
                      ...prev,
                      statusFilter:
                        filteredCodes?.length || statuses?.length ? {statuses, codes: filteredCodes} : undefined,
                    })
                  );
                },
              })),
            },
          ].filter(Boolean);
        },
        items: [premiumTypeDialogItem, dateRangeDialogItem, datasourceTypeDialogItem, policyStatusDialogItem],
      }),
    [treeData, withPolicyStatusFilter]
  );
};

const shortenedProductionAllocationOptions = [
  {
    label: 'Production Credit',
    value: DatasourceType.ProductionCredit,
  },
  {
    label: 'Primary Producer',
    value: DatasourceType.AgencyData,
  },
];

const shortenedPolicyStatusOptions = [
  {
    label: 'AMS',
    value: PolicyStatusType.Ams,
  },
  {
    label: 'ennabl',
    value: PolicyStatusType.Ennabl,
  },
];

export const usePeriodTopbarItems = () => {
  const withPolicyStatusFilter = useFeatureFlag(FeatureFlags.PolicyStatusFilter);

  return useMemo(
    () => [
      createTopbarRadioGroupItem({
        id: FilterTopbarItemId.PremiumType,
        dropdownLabel: 'Premium Type',
        getValue: filter => {
          if (!isPolicyFilter(filter)) {
            return;
          }

          return filter?.period?.amountType;
        },
        getLabel: value => (value ? getLabelByValue(amountTypeOptions, value) : 'Premium Type'),
        options: amountTypeOptions,
        onApply: (filter, value) => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            datasourceType:
              value === PolicyAmountType.AnnualizedWritten ? DatasourceType.AgencyData : filter.datasourceType,
            period: formatPolicyFilterPeriod({
              ...filter.period,
              amountType: value,
              type: value === PolicyAmountType.Billed ? PeriodType.Rolling_12Months : PeriodType.InForce,
              timestamp: value === PolicyAmountType.Billed ? filter.period?.timestamp : undefined,
              inclusionDateType:
                value === PolicyAmountType.Billed ? InclusionDateType.Transaction : filter.period?.inclusionDateType,
            }),
          };
        },
      }),
      createValueItem({
        id: FilterTopbarItemId.DateRange,
        getValue: filter => {
          if (!isPolicyFilter(filter) || !filter.period) {
            return;
          }

          return <SeparatedLabel labels={getPeriodTypeSeparatedLabels(filter.period)} />;
        },
        onClick: () => openFilterDialog(FilterDialogSectionId.DateAndType),
      }),
      createValueItem({
        id: FilterTopbarItemId.AdditionalDateRange,
        onClick: () => openFilterDialog(FilterDialogSectionId.DateAndType),
        getValue: filter => {
          if (!isPolicyFilter(filter) || !filter.additionalPeriods?.length) {
            return;
          }

          if (filter.additionalPeriods.length > 1) {
            throw new Error('Multiple additional periods are not supported in topbar');
          }

          const period = filter.additionalPeriods[0];

          return `Tr ${dayjs(period.from).format(DATE_FORMAT)} - ${dayjs(period.to).format(DATE_FORMAT)}`;
        },
        onReset: filter => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            additionalPeriods: undefined,
          };
        },
      }),
      createTopbarRadioGroupItem({
        id: FilterTopbarItemId.DatasourceType,
        dropdownLabel: 'Edit Production Allocation',
        getValue: filter => {
          if (!isPolicyFilter(filter)) {
            return;
          }

          return filter.datasourceType;
        },
        getLabel: value =>
          value ? getLabelByValue(shortenedProductionAllocationOptions, value) : 'Production Allocation',
        options: productionAllocationOptions,
        onApply: (filter, value) => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            datasourceType: value,
          };
        },
      }),
      withPolicyStatusFilter &&
        createTopbarRadioGroupItem({
          id: FilterTopbarItemId.PolicyStatusType,
          dropdownLabel: 'Edit Policy Status',
          getValue: filter => {
            if (!isPolicyFilter(filter)) {
              return;
            }

            return filter.policyStatusType;
          },
          getLabel: value => (value ? getLabelByValue(shortenedPolicyStatusOptions, value) : 'Policy Status'),
          options: policyStatusOptions,
          onApply: (filter, value) => {
            assertPolicyFilter(filter);

            return {
              ...filter,
              policyStatusType: value,
              policyStatusesAnd: undefined,
            };
          },
        }),
      createCounterItem({
        id: FilterTopbarItemId.PolicyStatus,
        entity: 'Policy Statuses',
        getCount: filter => {
          if (!isPolicyFilter(filter)) {
            return;
          }

          return filter.policyStatusesAnd?.[0]?.statuses?.length;
        },
        onClick: () => openFilterDialog(FilterDialogSectionId.DateAndType),
        onReset: filter => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            policyStatusesAnd: filter.policyStatusesAnd?.[0]?.codes?.length
              ? [{statuses: undefined, codes: filter.policyStatusesAnd?.[0]?.codes}]
              : undefined,
          };
        },
      }),
      createCounterItem({
        id: FilterTopbarItemId.PolicyStatusCode,
        entity: 'Policy AMS Codes',
        getCount: filter => {
          if (!isPolicyFilter(filter)) {
            return;
          }

          return filter.policyStatusesAnd?.[0]?.codes?.length;
        },
        onClick: () => openFilterDialog(FilterDialogSectionId.DateAndType),
        onReset: filter => {
          assertPolicyFilter(filter);

          return {
            ...filter,
            policyStatusesAnd: filter.policyStatusesAnd?.[0]?.statuses?.length
              ? [{statuses: filter.policyStatusesAnd?.[0]?.statuses, codes: undefined}]
              : undefined,
          };
        },
      }),
    ],
    [withPolicyStatusFilter]
  );
};
